import { useCallback, useRef } from 'react'

import { useAiChatBotStore } from '../../core/store/useAiChatBotStore'

export function useFabDrag() {
  const dragging = useRef(false)
  const wasDragging = useRef(false)
  const startPointer = useRef({ x: 0, y: 0 })
  const startPos = useRef({ x: 0, y: 0 })
  // Base (untransformed) viewport position captured at pointerdown — fixed reference for clamping
  const baseRect = useRef({ left: 0, top: 0, width: 0, height: 0 })

  // Callback ref — fires on every mount/unmount of the button so listeners
  // are always fresh even when FloatingButton remounts after panel close.
  // Special case: state is read via getState() inside the ref/handlers (not via
  // a selector) — subscribing would remount the button and reset drag state on
  // every position change.
  const ref = useCallback((el: HTMLButtonElement | null) => {
    if (!el) return
    const node = el

    const { fabPosition } = useAiChatBotStore.getState()

    if (fabPosition) {
      node.style.transform = `translate(${fabPosition.x}px, ${fabPosition.y}px)`
    }

    function onPointerDown(e: PointerEvent) {
      if (e.button !== 0) return
      dragging.current = false
      wasDragging.current = false
      startPointer.current = { x: e.clientX, y: e.clientY }
      const matrix = new DOMMatrixReadOnly(getComputedStyle(node).transform)
      startPos.current = { x: matrix.m41, y: matrix.m42 }
      // Capture the current viewport rect before any new transform is applied
      const r = node.getBoundingClientRect()
      baseRect.current = {
        left: r.left,
        top: r.top,
        width: r.width,
        height: r.height,
      }
      node.setPointerCapture(e.pointerId)
    }

    function onPointerMove(e: PointerEvent) {
      if (!node.hasPointerCapture(e.pointerId)) return
      const dx = e.clientX - startPointer.current.x
      const dy = e.clientY - startPointer.current.y
      if (!dragging.current && Math.hypot(dx, dy) > 4) {
        dragging.current = true
      }
      if (!dragging.current) return

      const raw = {
        x: startPos.current.x + dx,
        y: startPos.current.y + dy,
      }

      // Clamp using the rect captured at pointerdown — stable reference, not live
      const { left, top, width, height } = baseRect.current
      const projectedLeft = left + (raw.x - startPos.current.x)
      const projectedTop = top + (raw.y - startPos.current.y)
      const clampedX =
        raw.x -
        Math.max(0, projectedLeft + width - window.innerWidth) +
        Math.max(0, -projectedLeft)
      const clampedY =
        raw.y -
        Math.max(0, projectedTop + height - window.innerHeight) +
        Math.max(0, -projectedTop)

      node.style.transform = `translate(${clampedX}px, ${clampedY}px)`
    }

    function onPointerUp(e: PointerEvent) {
      if (!node.hasPointerCapture(e.pointerId)) return
      node.releasePointerCapture(e.pointerId)
      if (!dragging.current) return

      // Mark as dragged BEFORE resetting dragging — onClick reads wasDragging
      wasDragging.current = true
      dragging.current = false

      const matrix = new DOMMatrixReadOnly(getComputedStyle(node).transform)
      useAiChatBotStore.getState().setFabPosition({
        x: matrix.m41,
        y: matrix.m42,
      })
    }

    function onClick(e: MouseEvent) {
      if (wasDragging.current) {
        e.stopImmediatePropagation()
        wasDragging.current = false
      }
    }

    node.addEventListener('pointerdown', onPointerDown)
    node.addEventListener('pointermove', onPointerMove)
    node.addEventListener('pointerup', onPointerUp)
    node.addEventListener('click', onClick, { capture: true })

    // React 19 callback ref cleanup
    return () => {
      node.removeEventListener('pointerdown', onPointerDown)
      node.removeEventListener('pointermove', onPointerMove)
      node.removeEventListener('pointerup', onPointerUp)
      node.removeEventListener('click', onClick, { capture: true })
    }
  }, [])

  return ref
}
